// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type CreateIlliquidAssetEventRequestBody,
  type CreateIlliquidAssetRequestBody,
  IlliquidAssetCurrency,
  IlliquidAssetEventType,
  IlliquidAssetType,
  type IlliquidAssetEvent,
} from '@shared/data-access/types/engine-types';
import type { IlliquidAssetFormData } from '../types/illiquid-asset-form';

export const mapDraftEventToCreateBody = (
  event: IlliquidAssetEvent,
): CreateIlliquidAssetEventRequestBody => {
  const base = {
    event_date: event.event_date,
    amount: event.amount ?? 0,
    currency: event.currency ?? IlliquidAssetCurrency.EUR,
    description: event.description ?? null,
  };

  switch (event.event_type) {
    case IlliquidAssetEventType.Acquisition:
      return {
        event_type: IlliquidAssetEventType.Acquisition,
        subtype: null,
        ...base,
      };

    case IlliquidAssetEventType.Valuation:
      return {
        event_type: IlliquidAssetEventType.Valuation,
        subtype: null,
        ...base,
      };

    case IlliquidAssetEventType.CapitalCall:
      return {
        event_type: IlliquidAssetEventType.CapitalCall,
        subtype: event.subtype,
        ...base,
      };

    case IlliquidAssetEventType.Distribution:
      return {
        event_type: IlliquidAssetEventType.Distribution,
        subtype: event.subtype,
        ...base,
      };
  }
};

export const mapDraftEventsToCreateBodies = (
  draftEvents: readonly IlliquidAssetEvent[],
): CreateIlliquidAssetEventRequestBody[] | undefined => {
  if (!draftEvents.length) {
    return undefined;
  }

  return draftEvents.map(mapDraftEventToCreateBody);
};

export const buildIlliquidAssetCreateBody = (
  data: IlliquidAssetFormData,
  draftEvents: readonly IlliquidAssetEvent[] = [],
): CreateIlliquidAssetRequestBody => {
  const events = mapDraftEventsToCreateBodies(draftEvents);

  switch (data.category) {
    case IlliquidAssetType.PeVc:
      return {
        category: IlliquidAssetType.PeVc,
        name: data.name,
        subcategory: data.subcategory,
        description: data.description ?? null,
        currency: data.currency,
        current_valuation: data.current_valuation,
        valuation_date: data.valuation_date,
        fund_manager: data.fund_manager,
        total_commitment: data.total_commitment,
        ...(events ? { events } : {}),
      };

    case IlliquidAssetType.RealEstate:
      return {
        category: IlliquidAssetType.RealEstate,
        name: data.name,
        subcategory: data.subcategory,
        description: data.description ?? null,
        currency: data.currency,
        current_valuation: data.current_valuation,
        valuation_date: data.valuation_date,
        acquisition_date: data.acquisition_date,
        acquisition_value: data.acquisition_value,
        ownership_pct: data.ownership_pct,
        ...(events ? { events } : {}),
      };

    case IlliquidAssetType.Company:
      return {
        category: IlliquidAssetType.Company,
        name: data.name,
        subcategory: data.subcategory,
        description: data.description ?? null,
        currency: data.currency,
        current_valuation: data.current_valuation,
        valuation_date: data.valuation_date,
        acquisition_date: data.acquisition_date,
        acquisition_value: data.acquisition_value,
        ownership_pct: data.ownership_pct,
        ...(events ? { events } : {}),
      };

    case IlliquidAssetType.Tangible:
      return {
        category: IlliquidAssetType.Tangible,
        name: data.name,
        subcategory: data.subcategory,
        description: data.description ?? null,
        currency: data.currency,
        current_valuation: data.current_valuation,
        valuation_date: data.valuation_date,
        acquisition_date: data.acquisition_date,
        acquisition_value: data.acquisition_value,
        ownership_pct: data.ownership_pct,
        ...(events ? { events } : {}),
      };

    case IlliquidAssetType.OtherIlliquid:
      return {
        category: IlliquidAssetType.OtherIlliquid,
        name: data.name,
        subcategory: data.subcategory,
        description: data.description ?? null,
        currency: data.currency,
        current_valuation: data.current_valuation,
        valuation_date: data.valuation_date,
        acquisition_date: data.acquisition_date,
        acquisition_value: data.acquisition_value,
        ownership_pct: data.ownership_pct,
        ...(events ? { events } : {}),
      };
  }
};
